/**
 * @author Nihar<nihar.baishya@314ecorp.com>
 * @description posthog tracker
 */

import _ from 'lodash';

type PosthogEvent = {
	eventName: string;
	properties: AnyRecord;
};

// all the actions that can be tracked
const ACTIONS = {
	download: 'download',
	select: 'select',
	click: 'click',
	search: 'search',
	view: 'view',
	add: 'add',
	delete: 'delete',
	clear: 'clear',
	export: 'export',
	edit: 'edit',
	upload: 'upload',
	change: 'change',
	run: 'run',
	schedule: 'schedule',
	enroll: 'enroll',
	apply: 'apply',
	expand: 'expand',
	print: 'print',
	submit: 'submit',
};

// all the event categories, it is equivalent to the feature name
const EVENT_CATEGORIES = {
	eligibility: 'Eligibility',
	codeExplorer: 'Code Explorer',
	onboarding: 'Onboarding',
};

// all the entities that can be tracked inside a category, it can ve screen type or subpart
const EVENT_ENTITIES = {
	// batch upload
	batchUpload: 'batch_upload',
	batchEdit: 'batch_edit',
	batchDetailedView: 'batch_detailed_view',
	batchList: 'batch_list',

	// eligibility request
	eligibilityRequest: 'eligibility_request',

	// eligibility response
	eligibilityResponse: 'eligibility_response',

	// code explorer
	codeSearch: 'code_search',
	searchSuggestions: 'search_suggestions',
	codeCard: 'code_card',
	codeDetailedView: 'code_detailed_view',

	// onboarding
	onboarding: 'onboarding',
};

// generic events names, it is reusable. But entity will be different for each event.
const EVENT_KEYS = {
	// batch eligibility
	fileUploadUpload: 'file_upload:upload',
	fileUploadClear: 'file_upload:clear',
	batchTemplateExport: 'batch_template:export',
	batchFieldsView: 'batch_fields:view',
	batchRequestRun: 'batch_request:run',
	batchRequestSchedule: 'batch_request:schedule',
	payerAdd: 'payer:add',
	payerEnrollmentEnroll: 'payer_enrollment:enroll',
	batchTransactionsExport: 'batch_transactions:export',
	batchDelete: 'batch:delete',
	batchEdit: 'batch:edit',
	batchResponseView: 'batch_detailed_view:view',
	// eligibility request
	eligibilityRequestSubmit: 'eligibility_request:submit',
	// eligibility response
	anchorNavClick: 'anchor_nav:click',
	anchorNavSearch: 'anchor_nav:search',
	networkFilterSelect: 'network_filter:select',
	printResponsePrint: 'print_response:print',
	// code explorer
	filters: 'filters:apply',
	codeDetailView: 'code_detailed_view:view',
	searchResultsView: 'search_results:view',
	copyCode: 'copy_code:click',
	codeHierarchyView: 'code_hierarchy:view',
	favouriteCodeAdd: 'favourite_code:add',
	favouriteCodeDelete: 'favourite_code:delete',
	codeNoteAdd: 'code_note:add',
	codeNoteEdit: 'code_note:edit',
	codeNoteDelete: 'code_note:delete',
	codeAttributeSearch: 'code_attribute:search',
	codeAttributeExpand: 'code_attribute:expand',
	// onboarding
	onboardingNewEligibilityRequest: 'onboarding:new_eligibility_request',
	onboardingCheckClaimStatus: 'onboarding:check_claim_status',
};

const CODE_EXPLORER = {
	filters: (codeSet: string[]): PosthogEvent => ({
		eventName: EVENT_KEYS.filters,
		properties: {
			category: EVENT_CATEGORIES.codeExplorer,
			entity: EVENT_ENTITIES.codeSearch,
			action: ACTIONS.apply,
			codeSet,
		},
	}),
	codeDetailedView: (code: string, codeSet: CodeExplorer.ICodeSetSystem, entity: string): PosthogEvent => ({
		eventName: EVENT_KEYS.codeDetailView,
		properties: {
			category: EVENT_CATEGORIES.codeExplorer,
			entity,
			action: ACTIONS.view,
			code,
			codeSet,
		},
	}),
	searchResults: ({ code, codeSet }: { code: string; codeSet: CodeExplorer.ICodeSetSystem[] }): PosthogEvent => ({
		eventName: EVENT_KEYS.searchResultsView,
		properties: {
			category: EVENT_CATEGORIES.codeExplorer,
			entity: EVENT_ENTITIES.searchSuggestions,
			action: ACTIONS.view,
			code,
			codeSet,
		},
	}),
	copyCode: (code: string, codeSet: string[], entity: string): PosthogEvent => ({
		eventName: EVENT_KEYS.copyCode,
		properties: {
			category: EVENT_CATEGORIES.codeExplorer,
			entity,
			action: ACTIONS.click,
			code,
			codeSet,
		},
	}),
	codeHierarchy: (code: string, codeSet: CodeExplorer.ICodeSetSystem, entity: string): PosthogEvent => ({
		eventName: EVENT_KEYS.codeHierarchyView,
		properties: {
			category: EVENT_CATEGORIES.codeExplorer,
			entity,
			action: ACTIONS.view,
			code,
			codeSet,
		},
	}),
	favoriteAdded: (code: string, codeSet: CodeExplorer.ICodeSetSystem, entity: string): PosthogEvent => ({
		eventName: EVENT_KEYS.favouriteCodeAdd,
		properties: {
			category: EVENT_CATEGORIES.codeExplorer,
			entity,
			action: ACTIONS.add,
			code,
			codeSet,
		},
	}),
	favoriteDeleted: (code: string, codeSet: CodeExplorer.ICodeSetSystem, entity: string): PosthogEvent => ({
		eventName: EVENT_KEYS.favouriteCodeDelete,
		properties: {
			category: EVENT_CATEGORIES.codeExplorer,
			entity,
			action: ACTIONS.delete,
			code,
			codeSet,
		},
	}),
	internalNoteAdded: (code: string, codeSet: CodeExplorer.ICodeSetSystem, entity: string): PosthogEvent => ({
		eventName: EVENT_KEYS.codeNoteAdd,
		properties: {
			category: EVENT_CATEGORIES.codeExplorer,
			entity,
			action: ACTIONS.add,
			code,
			codeSet,
		},
	}),
	internalNoteEdited: (code: string, codeSet: CodeExplorer.ICodeSetSystem, entity: string): PosthogEvent => ({
		eventName: EVENT_KEYS.codeNoteEdit,
		properties: {
			category: EVENT_CATEGORIES.codeExplorer,
			entity,
			action: ACTIONS.edit,
			code,
			codeSet,
		},
	}),
	internalNoteDeleted: (code: string, codeSet: CodeExplorer.ICodeSetSystem, entity: string): PosthogEvent => ({
		eventName: EVENT_KEYS.codeNoteDelete,
		properties: {
			category: EVENT_CATEGORIES.codeExplorer,
			entity,
			action: ACTIONS.delete,
			code,
			codeSet,
		},
	}),
	searchCodeAttribute: (code: string, searchTerm: string): PosthogEvent => ({
		eventName: EVENT_KEYS.codeAttributeSearch,
		properties: {
			category: EVENT_CATEGORIES.codeExplorer,
			entity: EVENT_ENTITIES.codeDetailedView,
			action: ACTIONS.search,
			code,
			searchTerm,
		},
	}),
	codeAttributeSeeMoreClicked: (code: string): PosthogEvent => ({
		eventName: EVENT_KEYS.codeAttributeExpand,
		properties: {
			category: EVENT_CATEGORIES.codeExplorer,
			entity: EVENT_ENTITIES.codeDetailedView,
			action: ACTIONS.expand,
			code,
		},
	}),
};

const ELIGIBILITY_BATCH_UPLOAD = {
	uploadFile: (): PosthogEvent => ({
		eventName: EVENT_KEYS.fileUploadUpload,
		properties: {
			category: EVENT_CATEGORIES.eligibility,
			entity: EVENT_ENTITIES.batchUpload,
			action: ACTIONS.upload,
		},
	}),
	clearUploadedFile: (): PosthogEvent => ({
		eventName: EVENT_KEYS.fileUploadClear,
		properties: {
			category: EVENT_CATEGORIES.eligibility,
			entity: EVENT_ENTITIES.batchUpload,
			action: ACTIONS.clear,
		},
	}),
	exportBatchTemplate: (): PosthogEvent => ({
		eventName: EVENT_KEYS.batchTemplateExport,
		properties: {
			category: EVENT_CATEGORIES.eligibility,
			entity: EVENT_ENTITIES.batchUpload,
			action: ACTIONS.export,
		},
	}),
	viewMandatoryOptionalFields: (): PosthogEvent => ({
		eventName: EVENT_KEYS.batchFieldsView,
		properties: {
			category: EVENT_CATEGORIES.eligibility,
			entity: EVENT_ENTITIES.batchUpload,
			action: ACTIONS.view,
		},
	}),
	deleteBatch: (sequenceNumber: string, entity: string): PosthogEvent => ({
		eventName: EVENT_KEYS.batchDelete,
		properties: {
			category: EVENT_CATEGORIES.eligibility,
			entity,
			action: ACTIONS.delete,
			sequenceNumber,
		},
	}),
	runBatch: (sequenceNumber: string, totalRequests: number, invalidRequests: number): PosthogEvent => ({
		eventName: EVENT_KEYS.batchRequestRun,
		properties: {
			category: EVENT_CATEGORIES.eligibility,
			entity: EVENT_ENTITIES.batchEdit,
			action: ACTIONS.run,
			totalRequests,
			invalidRequests,
			sequenceNumber,
		},
	}),
	scheduleBatch: (sequenceNumber: string, totalRequests: number, invalidRequests: number): PosthogEvent => ({
		eventName: EVENT_KEYS.batchRequestSchedule,
		properties: {
			category: EVENT_CATEGORIES.eligibility,
			entity: EVENT_ENTITIES.batchEdit,
			action: ACTIONS.schedule,
			totalRequests,
			invalidRequests,
			sequenceNumber,
		},
	}),
	addPayer: (payerID: string): PosthogEvent => ({
		eventName: EVENT_KEYS.payerAdd,
		properties: {
			category: EVENT_CATEGORIES.eligibility,
			entity: EVENT_ENTITIES.batchEdit,
			action: ACTIONS.add,
			payerID,
		},
	}),
	changeEnrollment: (payerID: string): PosthogEvent => ({
		eventName: EVENT_KEYS.payerEnrollmentEnroll,
		properties: {
			category: EVENT_CATEGORIES.eligibility,
			entity: EVENT_ENTITIES.batchEdit,
			action: ACTIONS.enroll,
			payerID,
		},
	}),
	exportTransactions: (sequenceNumber: number | string, exportType: string, entity: string): PosthogEvent => ({
		eventName: EVENT_KEYS.batchTransactionsExport,
		properties: {
			category: EVENT_CATEGORIES.eligibility,
			entity,
			action: ACTIONS.export,
			sequenceNumber: _.toString(sequenceNumber),
			exportType,
		},
	}),
	editBatch: (sequenceNumber: string): PosthogEvent => ({
		eventName: EVENT_KEYS.batchEdit,
		properties: {
			category: EVENT_CATEGORIES.eligibility,
			entity: EVENT_ENTITIES.batchList,
			action: ACTIONS.edit,
			sequenceNumber,
		},
	}),
	viewBatchResponse: (sequenceNumber: string): PosthogEvent => ({
		eventName: EVENT_KEYS.batchResponseView,
		properties: {
			category: EVENT_CATEGORIES.eligibility,
			entity: EVENT_ENTITIES.batchList,
			action: ACTIONS.view,
			sequenceNumber,
		},
	}),
};

type EligibilityRequestType = 'new' | 'edit' | 'resubmit';

const ELIGIBILITY_REQUEST = {
	submit: (requestType: EligibilityRequestType): PosthogEvent => ({
		eventName: EVENT_KEYS.eligibilityRequestSubmit,
		properties: {
			category: EVENT_CATEGORIES.eligibility,
			entity: EVENT_ENTITIES.eligibilityRequest,
			action: ACTIONS.submit,
			requestType,
		},
	}),
};

const ELIGIBILITY_RESPONSE = {
	anchorNavClick: (): PosthogEvent => ({
		eventName: EVENT_KEYS.anchorNavClick,
		properties: {
			category: EVENT_CATEGORIES.eligibility,
			entity: EVENT_ENTITIES.eligibilityResponse,
			action: ACTIONS.click,
		},
	}),
	anchorNavSearch: (): PosthogEvent => ({
		eventName: EVENT_KEYS.anchorNavSearch,
		properties: {
			category: EVENT_CATEGORIES.eligibility,
			entity: EVENT_ENTITIES.eligibilityResponse,
			action: ACTIONS.search,
		},
	}),
	networkFilterSelect: (): PosthogEvent => ({
		eventName: EVENT_KEYS.networkFilterSelect,
		properties: {
			category: EVENT_CATEGORIES.eligibility,
			entity: EVENT_ENTITIES.eligibilityResponse,
			action: ACTIONS.select,
		},
	}),
	printResponse: (): PosthogEvent => ({
		eventName: EVENT_KEYS.printResponsePrint,
		properties: {
			category: EVENT_CATEGORIES.eligibility,
			entity: EVENT_ENTITIES.eligibilityResponse,
			action: ACTIONS.print,
		},
	}),
};

const ONBOARDING = {
	newEligibilityRequest: (): PosthogEvent => ({
		eventName: EVENT_KEYS.onboardingNewEligibilityRequest,
		properties: {
			category: EVENT_CATEGORIES.onboarding,
			entity: EVENT_ENTITIES.onboarding,
			action: ACTIONS.submit,
		},
	}),
	checkClaimStatus: (): PosthogEvent => ({
		eventName: EVENT_KEYS.onboardingCheckClaimStatus,
		properties: {
			category: EVENT_CATEGORIES.onboarding,
			entity: EVENT_ENTITIES.onboarding,
			action: ACTIONS.submit,
		},
	}),
};

// all events
const EVENTS = {
	codeExplorer: CODE_EXPLORER,
	eligibilityBatchUpload: ELIGIBILITY_BATCH_UPLOAD,
	eligibilityRequest: ELIGIBILITY_REQUEST,
	eligibilityResponse: ELIGIBILITY_RESPONSE,
	onboarding: ONBOARDING,
};

export { EVENTS, EVENT_ENTITIES, PosthogEvent };
